import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Key, LogOut, Home } from "lucide-react";
import MiniFooter from "./MiniFooter";
import ChileIndicators from "./ChileIndicators";

export default function DashboardLayout({ title, subtitle, items, children, testId = "dashboard" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({});

  const refresh = () => {
    api.get("/notifications/unread-counts").then((r) => setCounts(r.data || {})).catch(() => {});
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, []);

  // When navigating to a section, mark its notifications as read after a short delay.
  useEffect(() => {
    const sec = sectionFromPath(location.pathname);
    if (!sec) return;
    const t = setTimeout(() => {
      api.post(`/notifications/mark-read-by-section?section=${sec}`).then(refresh).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <aside className="w-64 bg-white border-r border-zinc-200 hidden lg:flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-zinc-200">
          <NavLink to="/" className="block">
            <img src="/logo-brand.png" alt="DerechoLlave" className="w-full h-auto max-w-[200px]" />
          </NavLink>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => {
            const sec = it.section;
            const badge = sec ? counts[sec] || 0 : 0;
            return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`sidebar-${it.testId || it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-zinc-100 hover:text-slate-900"
                }`
              }
            >
              {it.icon}
              <span>{it.label}</span>
              {badge > 0 && (
                <span className="ml-auto text-[10px] bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-bold animate-pulse" data-testid={`badge-${sec}`}>
                  {badge > 9 ? "9+" : badge}
                </span>
              )}
            </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-zinc-200 space-y-1">
          <NavLink to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-600 hover:bg-zinc-100">
            <Home className="w-4 h-4" /> Ir al sitio
          </NavLink>
          <button onClick={() => { logout(); navigate("/"); }} className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-rose-700 hover:bg-rose-50" data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col" data-testid={testId}>
        <header className="bg-white border-b border-zinc-200 px-6 lg:px-8 py-5 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-display text-xl lg:text-2xl font-bold tracking-tight">{subtitle}</h1>
            <p className="text-xs text-slate-500">{user?.full_name} · {user?.email}</p>
          </div>
          <ChileIndicators variant="light" />
        </header>
        <main className="p-6 lg:p-8 flex-1">{children}</main>
        <MiniFooter />
      </div>
    </div>
  );
}

function sectionFromPath(path) {
  // Map URL to notification section so visiting it clears the badge
  if (path.startsWith("/admin/businesses") || path.startsWith("/seller/businesses")) return "businesses";
  if (path.startsWith("/admin/requests") || path.startsWith("/seller/requests") || path.startsWith("/investor/requests")) return "requests";
  if (path.startsWith("/admin/documents") || path.startsWith("/investor/documents")) return "documents";
  if (path.startsWith("/admin/messages")) return "messages";
  if (path.includes("/conversations/")) return path.startsWith("/investor") ? "panel" : "messages";
  return null;
}
